// Styles
import classnames from 'classnames'
import styled from 'styled-components/macro'

// Core
import React, {Dispatch, Fragment, SetStateAction, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {useHistory, useLocation} from 'react-router-dom'

// Components, services, etc
import {openModal} from 'actions/modalActions'
import {createOrder} from 'actions/ordersActions'
import Button from 'components/Button'
import Icon from 'components/Icon'
import ProductOrders from 'components/ProductCard/ProductOrders'
import {CLICKED_LEARN_MORE} from 'constants/mixpanelConstants'
import {LOG_IN_SIGN_UP_MODAL} from 'constants/modalsConstants'
import {
  CDA,
  CLEARPROP,
  productDataDictionaryHomePage,
  productDataDictionaryNonHomePage
} from 'constants/productCardConstants'
import type {ProductData} from 'constants/productCardConstants'
import {propertyDataSelector, isUserAuthenticatedSelector} from 'selectors'
import {isInvoicePaySelector} from 'selectors/customerSelectors'
import {createSuppliedAddressObject} from 'services/addressServices'
import {getMixpanelPage, trackMixpanelEvent} from 'services/mixpanel'
import {isExisty} from 'services/utils'
import type {CreateOrderRequestBody, OpsOrderType, ProductType} from 'types/orderTypes'

// 3rd-party
import type {ChipProps} from 'components/Chip/Chip'
import {ALL_ORDERS_PATH} from 'constants/appRoutesConstants'
import {getProductNavPath} from 'services/productNavigationServices'
import type {PropertyType} from 'types/propertyTypes'

type Props = {
  index?: number
  orders?: Array<OpsOrderType>
  'data-sqa-id'?: string
  productType: ProductType
}

type ProductCardProps = {
  handleBuyNow: React.MouseEventHandler<HTMLButtonElement>
  productData: ProductData
  submitting: boolean
  handleLearnMoreClick: React.MouseEventHandler<HTMLButtonElement>
  isInvoicePay: boolean
  productType: ProductType
  isHovered: boolean
  setIsHovered: Dispatch<SetStateAction<boolean>>
  index?: number
  isUserOnHomePage?: boolean
  isLoggedIn?: boolean
} & Pick<Props, 'orders'> &
  Pick<PropertyType, 'pid'>

const ProductCard = ({index, productType, orders}: Props) => {
  const dispatch = useDispatch()
  const history = useHistory()
  const location = useLocation()
  const isUserOnHomePage = location.pathname === '/products'
  const [submitting, setSubmitting] = useState(false)
  const propertyData = useSelector(propertyDataSelector)
  const productData = isUserOnHomePage
    ? productDataDictionaryHomePage[productType]
    : productDataDictionaryNonHomePage[productType]
  const {pid} = propertyData ?? {}
  const isInvoicePay = useSelector(isInvoicePaySelector)
  const [isHovered, setIsHovered] = useState(false)
  const isLoggedIn = useSelector(isUserAuthenticatedSelector)
  const shouldShowPrice = isExisty(productData.price) && isLoggedIn
  const hasOrders = Boolean(orders && orders?.length > 0)

  const handleBuyNow = () => {
    if (isUserOnHomePage) {
      handleMixpanelLearnMoreEvent()
      window.open(productData.learnMoreLink)
    } else {
      if (isLoggedIn) {
        const suppliedAddress = createSuppliedAddressObject(propertyData)
        const placeOrder = (
          order: Omit<CreateOrderRequestBody, 'suppliedAddress'>,
          orderPrice?: string
        ) => {
          return dispatch(
            createOrder({
              orderPayload: {...order, suppliedAddress},
              handleOrder,
              handleOrderError,
              handleOrderAbandoned,
              mixpanelEventData: {
                page: getMixpanelPage(location.pathname),
                component: 'product card'
              },
              orderPrice: Number(orderPrice ?? '')
            })
          )
        }

        if (productType === CLEARPROP) {
          setSubmitting(true)
          placeOrder({pid, productType: CLEARPROP})
        } else if (productType === CDA) {
          return dispatch(
            openModal({
              modalType: 'CDA_CHECKOUT_MODAL',
              property: propertyData,
              placeOrder
            })
          )
        }
      } else {
        dispatch(
          openModal({
            modalType: LOG_IN_SIGN_UP_MODAL,
            mode: 'signUp',
            mixpanelEventData: {
              initiatedFrom: 'Product Card'
            }
          })
        )
      }
    }
  }
  const handleOrder = (order: OpsOrderType) => {
    setSubmitting(false)

    if (order.productOrderId) {
      history.push(getProductNavPath(order))
    } else {
      history.push(ALL_ORDERS_PATH)
    }
  }
  const handleOrderError = () => {
    setSubmitting(false)
    // TODO: why submit failed go to orders?
    history.push(ALL_ORDERS_PATH)
  }

  const handleOrderAbandoned = () => setSubmitting(false)

  const handleMixpanelLearnMoreEvent = () => {
    const page = getMixpanelPage(location.pathname)
    trackMixpanelEvent(CLICKED_LEARN_MORE, {product: productType, page})
  }

  return (
    <ProductCard.Styled className='product-card' $productColor={productData.productColor}>
      <div className='side left'>
        <div
          className={classnames('image-wrapper', {'learn-more-background': isHovered})}
          onClick={() => {
            handleMixpanelLearnMoreEvent()
            window.open(productData.learnMoreLink)
          }}
        >
          {isHovered && (
            <Button
              variant='secondary'
              title={
                <Fragment>
                  <span className='text'>Learn More</span>
                  <Icon className='launch-icon' icon='launch' />
                </Fragment>
              }
              className='learn-more-button'
              sqaPrefix={`learn-more-${index}`}
            />
          )}
          <img
            src={productData.promoSvg}
            alt={`${productData.name} Product`}
            className={classnames('product-image', {'learn-more-background': isHovered})}
            onMouseOver={e => {
              setIsHovered(true)
            }}
            onMouseOut={e => {
              setIsHovered(false)
            }}
            data-sqa-id={`image-${index}`}
          />
        </div>
        <h5 className='title' data-sqa-id={`title-${index}`}>
          {productData.title}
        </h5>
        {shouldShowPrice && (
          <div className='price' data-sqa-id={`price-${index}`}>
            {` ${productType === CDA ? 'From' : ''} $${productData.price}`}
          </div>
        )}
        <p
          className='description'
          data-sqa-id={`description-${index}`}
        >{`${productData.popDescription} `}</p>
        <div className='actions'>
          <Button
            className={classnames('action-button', {'action-button-width': hasOrders})}
            variant='secondary'
            title={
              <ProductCard.ButtonLabel
                isLoggedIn={isLoggedIn}
                submitting={submitting}
                productType={productType}
                isInvoicePay={isInvoicePay}
                hasOrders={hasOrders}
                isUserOnHomePage={isUserOnHomePage}
              />
            }
            onClick={handleBuyNow}
            disabled={submitting}
            sqaPrefix={`action-${index}`}
          />
          <ProductOrders orders={orders} />
        </div>
      </div>
    </ProductCard.Styled>
  )
}

ProductCard.ButtonLabel = ({
  isLoggedIn,
  isUserOnHomePage,
  submitting,
  hasOrders
}: Pick<
  ProductCardProps,
  'isLoggedIn' | 'submitting' | 'isInvoicePay' | 'productType' | 'isUserOnHomePage'
> & {hasOrders: boolean}) => {
  let content

  if (isUserOnHomePage) {
    content = (
      <Fragment>
        Learn More
        <Icon className='launch-icon' icon='launch' />
      </Fragment>
    )
  } else if (isLoggedIn) {
    content = submitting ? (
      <span>Ordering&hellip;</span>
    ) : (
      <React.Fragment>{hasOrders ? 'Order' : 'Order Now'}</React.Fragment>
    )
  } else {
    content = <React.Fragment>Get Started</React.Fragment>
  }

  return content
}

ProductCard.Styled = styled.div<{$productColor: ChipProps['color']}>`
  width: 273px;
  .description {
    margin-top: 0;
    margin-bottom: 13px;
    font-size: 0.875rem;
    line-height: 1.25rem;
    color: ${({theme}) => theme.colors.grayscale.gray};
  }

  .launch-icon {
    margin-left: 8px;
    font-size: 0.9rem;
  }

  &.product-card {
    padding: 0;
    display: flex;

    .side {
      flex: 1 0 auto;
      width: 100%;

      &.left {
        .product-image {
          width: 100%;
          height: 100%;
          border-radius: 5px;
          z-index: 50;
          :hover {
            opacity: 0.5;
            transition: all 0.2s ease-out;
            z-index: 25;
          }
        }
        .image-wrapper {
          position: relative;
          border-radius: 5px;
          width: 259px;
          height: 160px;
          cursor: pointer;

          &.learn-more-background {
            background: ${({theme}) => theme.colors.grayscale.black};
          }

          .learn-more-button {
            pointer-events: none;
            position: absolute;
            transform: translate(-50%, -50%);
            top: 50%;
            left: 50%;
            z-index: 10;
            min-width: 132px;
          }
        }

        .title {
          font-size: 1rem;
          margin-top: 16px;
          white-space: nowrap;
        }

        .price {
          margin-bottom: 8px;
        }

        .description {
          margin-bottom: 16px;
          width: 259px;
          .trimmed-content-container {
            .content-wrap {
              height: 41px;

              p {
                font-size: 0.875rem;
                line-height: 1.25rem;
                color: ${({theme}) => theme.colors.grayscale.gray};
              }
            }
          }
        }

        .actions {
          display: flex;
          align-items: center;
          .action-button {
            width: 100%;
            color: ${({theme}) => theme.colors.grayscale.white};
            background: ${({theme}) => theme.colors.grayscale.black};
            &.action-button-width {
              width: 71px;
            }
          }
          p {
            color: ${({theme}) => theme.colors.grayscale.gray};
            margin: 0 0 0 auto;
          }
          h5 {
            margin-bottom: 0;
            margin-left: 6px;
            font-weight: 400;
            display: inline;
          }
        }

        .not-available {
          margin-left: -2.5%;
          margin-bottom: 0;
          width: 105%;
        }
      }

      &.right {
        background-color: ${({theme, $productColor}) => theme.colors[$productColor].lightest};
        display: flex;
        align-items: flex-end;
        justify-content: center;
        padding: 40px 40px 0;

        img {
          width: 100%;
        }
      }
    }
  }
`

export default ProductCard
